import { FieldRecommendation } from "@/types/field";
import { Chip, Tooltip } from "@heroui/react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faExclamationCircle,
    faClockRotateLeft,
} from "@fortawesome/free-solid-svg-icons";

function RecommendationChip({
    recommendation,
    notes,
    user,
    deferDoNotApply,
    fullWidth,
}: {
    recommendation: FieldRecommendation;
    notes?: string;
    user?: string;
    deferDoNotApply?: boolean;
    fullWidth?: boolean;
}) {
    const { t } = useTranslation("home-dashboard");
    let rec = undefined;
    if (recommendation) {
        if (
            recommendation.user_recommendation != null &&
            recommendation.user_recommendation.recommend_application != null
        ) {
            rec = recommendation.user_recommendation.recommend_application
                ? t("apply_n")
                : t("do_not_apply_n");
        } else if (recommendation.gap) {
            rec = t("gap");
        } else if (recommendation.recent_application) {
            rec = t("recent_application");
        } else if (recommendation.insights_active && !recommendation.si_mean) {
            if (recommendation.imagery_id) {
                rec = t("no_insights");
            } else {
                rec = t("no_imagery");
            }
        } else if (
            recommendation?.recommend_fertigation === undefined ||
            recommendation?.recommend_fertigation === null
        ) {
            rec = undefined;
        } else {
            rec = recommendation?.recommend_fertigation
                ? t("apply_n")
                : t("do_not_apply_n");
        }
    }

    if (!rec || (deferDoNotApply && rec === t("do_not_apply_n"))) {
        return null;
    }

    return (
        <div
            className={classNames(
                "flex flex-row gap-2 items-center overflow-hidden",
                {
                    "w-full": fullWidth,
                },
            )}
        >
            <Chip
                radius="sm"
                size="sm"
                className={fullWidth ? "flex-1 max-w-full" : ""}
                classNames={{
                    base: classNames({
                        "text-white": true,
                        "bg-red-100": [
                            t("apply_n"),
                            t("no_insights"),
                            t("no_imagery"),
                        ].includes(rec),
                        "bg-green-100": rec == t("do_not_apply_n"),
                        "bg-gray-100": [
                            t("gap"),
                            t("recent_application"),
                        ].includes(rec),
                        "!flex-1 !max-w-full !flex !justify-center": fullWidth,
                    }),
                    content: classNames({
                        "font-bold": true,
                        "text-red-700": [
                            t("apply_n"),
                            t("no_insights"),
                            t("no_imagery"),
                        ].includes(rec),
                        "text-green-700": rec == t("do_not_apply_n"),
                        "text-gray-700": [
                            t("gap"),
                            t("recent_application"),
                        ].includes(rec),
                        "!justify-center !w-full !text-center": fullWidth,
                    }),
                }}
            >
                {[
                    t("apply_n"),
                    t("gap"),
                    t("no_insights"),
                    t("no_imagery"),
                ].includes(rec) && (
                    <FontAwesomeIcon
                        icon={faExclamationCircle}
                        className="mr-1"
                    />
                )}
                {rec === t("recent_application") && (
                    <FontAwesomeIcon
                        icon={faClockRotateLeft}
                        className="mr-1"
                    />
                )}
                {rec}
            </Chip>
            {notes && (
                <Tooltip
                    closeDelay={0}
                    content={
                        <div className="max-w-[20rem]">
                            <div className="font-semibold text-lg">Notes:</div>
                            <div>{notes}</div>
                            <div className=" text-gray-500">- {user}</div>
                        </div>
                    }
                >
                    <div className="text-xs overflow-ellipses whitespace-nowrap w-[5rem]">
                        {notes}
                    </div>
                </Tooltip>
            )}
        </div>
    );
}

export default RecommendationChip;
